import React, { useMemo } from 'react';
import { Platform } from 'react-native';

import { type ImageProps } from './Image.types';
import { getImageForCurrentScreen, withHttpsProtocol } from './Image.utils';
import { BaseImage } from './subcomponents/BaseImage';
import { ResponsiveImage } from './subcomponents/ResponsiveImage';

// ─────────────────────────────────────────────────────────────────────────────

export const Image = (props: ImageProps) => {
  const { source, responsive, 'aria-label': ariaLabel, ...rest } = props;
  const formattedSource = useMemo(
    () => getImageForCurrentScreen(withHttpsProtocol(source)),
    [source],
  );

  // ─────────────────────────────────────────────────────────────────────

  return responsive ? (
    <ResponsiveImage
      source={formattedSource}
      accessibilityRole={ariaLabel ? CONDITIONAL_ACCESSIBILITY_ROLE : undefined}
      aria-label={ariaLabel}
      {...rest}
    />
  ) : (
    <BaseImage
      source={formattedSource}
      accessibilityRole={ariaLabel ? CONDITIONAL_ACCESSIBILITY_ROLE : undefined}
      aria-label={ariaLabel}
      {...rest}
    />
  );
};

// ─── Constants ───────────────────────────────────────────────────────────────

/**
 * React Native for Web renders images in a `div` element that contains the
 * following children:
 *
 * - another `div` element with an image as the `background-image`
 * - an `img` element with a conditional `alt` attribute for SEO and
 *   accessibility.
 *
 * When we provide the `aria-label` prop, it is added to both the wrapper
 * `div` and a child `img` elements.
 *
 * The issue is that when it adds the prop to the wrapper `div`, a11y linters
 * like axe will complain because we now have a `div` with `aria-label`
 * but without `role`.
 * That is not valid in terms of web accessibility.
 *
 * To prevent this, we conditionally apply the `role="img"` attribute on
 * a web platform.
 */
const CONDITIONAL_ACCESSIBILITY_ROLE = Platform.select({
  web: 'image' as const,
  default: undefined,
});
